import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, join, sortBy, uniq, compact, values } from 'lodash';
import i18n from '../../utils/i18n';
import ASQFinishSurveyButton from './ASQFinishSurveyButton';
import ASQQuestion from './ASQQuestion';

const getWarningMessage = (sections) => {
  const qNum = join(sortBy(uniq(compact(sections.map(s => s.requiredAnswersCount)))), ',');
  return i18n.t('asq.survey.scoringWarning', { count: qNum });
}

const ASQSurvey = ({ isValid, survey, onChange, onDelete, onGoBack, onFinishSurvey }) => {
  // Answer ids we have already dispatched a DELETE for, shared across every
  // question so an orphan is deleted at most once for the whole survey. An id is
  // added once and never removed — see ASQQuestion for why releasing it would
  // recreate the write-storm.
  const deletingRef = useRef(new Set());

  if (isEmpty(survey)) return null;
  if (survey.entities.surveys[survey.result].status === 'finished') {
    onGoBack();
    return null;
  }
  const entities = survey.entities;
  const asq = entities.surveys[survey.result];
  // normalizr omits `answers` entirely when the survey has none loaded yet.
  const answersById = entities.answers || {};
  const sEntities = entities.questionnaires[asq.questionnaire].sections;
  const sLength = sEntities.length - 1;
  const sections = sEntities.map((sKey, sIdx) => {
    const s = entities.sections[sKey];
    const qLength = s.questions.length - 1;
    const questions = s.questions.map((q, qIdx) => {
      const question = entities.questions[q];
      return (
        <ASQQuestion
          key={`s${s.id}-q${q}`}
          asq={asq}
          question={question}
          entities={entities}
          answersById={answersById}
          qIdx={qIdx}
          qLength={qLength}
          onChange={onChange}
          onDelete={onDelete}
          deletingRef={deletingRef}
        />
      );
    });
    return (
      <section id={`section-${sIdx + 1}`} key={`section-${s.id}`} className='o-section'>
        <div className='u-txt--survey-title u-margin-bottom--rem'>{s.name}</div>
        {questions}
        {(sIdx !== sLength) && <hr className='u-hr--section' />}
      </section>
    )
  })
  return (
    <div id='sp-asq-survey' className='column o-sections o-sections--base u-margin-top--large u-margin-bottom--base cs-bg--light'>
      {sections}
      <ASQFinishSurveyButton
        isValid={isValid}
        onFinishSurvey={onFinishSurvey}
        message={isValid ? null : getWarningMessage(values(entities.sections))}
      />
    </div>
  );
}

ASQSurvey.propTypes = {
  isValid: PropTypes.bool.isRequired,
  survey: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onFinishSurvey: PropTypes.func.isRequired,
  onGoBack: PropTypes.func.isRequired,
}

export default ASQSurvey;
